import React, { useMemo, useState } from "react";
import { CheckCircle2, Search, ShieldCheck } from "lucide-react";

export function PageHeading({ eyebrow, title, description, actions }) {
  return (
    <div className="page-heading">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {actions ? <div className="page-actions">{actions}</div> : null}
    </div>
  );
}

export function CardHeader({ step, title, description, action }) {
  return (
    <div className="card-header">
      {step ? <span className="step-number">{step}</span> : null}
      <div>
        <h2>{title}</h2>
        {description ? <p>{description}</p> : null}
      </div>
      {action ? <div className="card-action">{action}</div> : null}
    </div>
  );
}

export function MetricCard({ value, label, note, tone = "accent" }) {
  return (
    <div className={`metric-card ${tone}`}>
      <strong>{value}</strong>
      <span>{label}</span>
      {note ? <small>{note}</small> : null}
    </div>
  );
}

export function RatingCard({ title, rating, compact = false }) {
  const assessed = rating?.score !== null && rating?.score !== undefined;
  const score = assessed ? rating.score : 0;
  return (
    <article className={`rating-card ${compact ? "compact" : ""}`}>
      <div
        className="rating-ring"
        style={{ "--score": score }}
        aria-label={assessed ? `${title}: ${score} out of 100` : `${title}: not assessed`}
      >
        <div>
          <strong>{assessed ? score : "—"}</strong>
          <span>{assessed ? "/100" : "N/A"}</span>
        </div>
      </div>
      <div className="rating-copy">
        <span>{title}</span>
        <div>
          <b>{rating?.grade || "—"}</b>
          <strong>{rating?.label || "Not assessed"}</strong>
        </div>
        {rating?.counts ? (
          <small>
            {rating.counts.error} errors · {rating.counts.warning} warnings
          </small>
        ) : null}
      </div>
    </article>
  );
}

export function SeverityBadge({ severity }) {
  return <span className={`severity-badge ${severity}`}>{capitalize(severity)}</span>;
}

export function StatusChip({ children, tone = "neutral", icon: Icon }) {
  return (
    <span className={`status-chip ${tone}`}>
      {Icon ? <Icon size={14} aria-hidden="true" /> : null}
      {children}
    </span>
  );
}

export function FindingsTable({
  findings,
  emptyMessage = "No findings match the current filters.",
  showSection = false,
  initialSection = "all",
}) {
  const [query, setQuery] = useState("");
  const [severity, setSeverity] = useState("all");
  const [section, setSection] = useState(initialSection);

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return findings.filter((item) => {
      if (severity !== "all" && item.severity !== severity) return false;
      if (section !== "all" && item.tabId !== section) return false;
      if (!normalized) return true;
      return [item.category, item.item, item.description, item.tabId]
        .join(" ")
        .toLowerCase()
        .includes(normalized);
    });
  }, [findings, query, section, severity]);

  return (
    <div className="findings-module">
      <div className="table-toolbar">
        <label className="search-field">
          <Search size={15} aria-hidden="true" />
          <span className="sr-only">Search findings</span>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search findings…"
          />
        </label>
        {showSection ? (
          <select
            aria-label="Filter findings by section"
            value={section}
            onChange={(event) => setSection(event.target.value)}
          >
            <option value="all">All sections</option>
            <option value="health">Health</option>
            <option value="naming">Naming</option>
            <option value="events">Events</option>
          </select>
        ) : null}
        <div className="filter-chips" role="group" aria-label="Filter findings by severity">
          {["all", "error", "warning", "info"].map((value) => (
            <button
              type="button"
              key={value}
              className={severity === value ? "active" : ""}
              onClick={() => setSeverity(value)}
            >
              {capitalize(value)}
            </button>
          ))}
        </div>
        <span className="result-count">{filtered.length} results</span>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Severity</th>
              {showSection ? <th>Section</th> : null}
              <th>Category</th>
              <th>Item</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length ? (
              filtered.map((item) => (
                <tr key={item.id}>
                  <td>
                    <SeverityBadge severity={item.severity} />
                  </td>
                  {showSection ? <td>{capitalize(item.tabId)}</td> : null}
                  <td>{item.category}</td>
                  <td className="item-cell">{item.item}</td>
                  <td className="description-cell">{item.description}</td>
                </tr>
              ))
            ) : (
              <tr className="empty-row">
                <td colSpan={showSection ? 5 : 4}>
                  <div className="empty-state">
                    <CheckCircle2 aria-hidden="true" />
                    <strong>{emptyMessage}</strong>
                    <span>Try another filter or import a different container.</span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function WelcomeEmpty({ onLoadDemo }) {
  return (
    <div className="welcome-empty">
      <span className="welcome-icon">
        <ShieldCheck aria-hidden="true" />
      </span>
      <p className="eyebrow">Private browser audit</p>
      <h2>Start with a GTM container export</h2>
      <p>
        Your JSON is parsed locally. Deterministic ratings work without AI, and every
        external AI provider remains optional.
      </p>
      <button type="button" className="secondary-button" onClick={onLoadDemo}>
        Explore with demo data
      </button>
    </div>
  );
}

function capitalize(value) {
  return value ? value[0].toUpperCase() + value.slice(1) : "";
}
